/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { memo, useCallback, useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import {
	useEmptyChatLaunchSelector,
	useSelectedSpaceSelector,
	useUIStore,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { CustomGreetingCard } from '@atlassian/conversation-assistant-ui-components/ui/CustomGreetingCard';
import { RovoGreeting } from '@atlassian/conversation-assistant-ui-components/ui/RovoGreeting';
import {
	type ConversationAction,
	type HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import { useReportInsightsBannerResolved } from '@atlassian/rovo-growth-pulse/common/analytics/use-report-insights-banner-resolved';
import { isInsightsScheduledDay } from '@atlassian/rovo-growth-pulse/common/utils/insights-schedule';
import { useInsightsStatus } from '@atlassian/rovo-growth-pulse/services/insights-status';
import { InsightsBanner } from '@atlassian/rovo-growth-pulse/ui/insights-banner';

import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../../common/utils/use-assistance-service-params';
import { useActiveAction } from '../../../controllers/active-action';
import { ConversationAssistantUIModes } from '../../../types';
import { usePlaceholderOverrideDispatch } from '../../chat-input-placeholder-override-context/usePlaceholderOverrideDispatch';

import { RovoEmptyStateConversationActions } from './conversation-actions-section';
import { PersonalityRovoGreeting } from './personality-rovo-greeting';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-end',
		height: '100%',
		width: '100%',
		marginTop: 'auto',
	},
	// Mini modal wrapper: minHeight 100% so the wrapper fills its scrollable parent's clientHeight,
	// and justifyContent flex-end pulls greeting + actions to the bottom edge — matching the
	// non-resizable sidebar wrapper above. Avoids the previous flex:1 + spacer<div> approach which
	// did not push to bottom when the parent panel had overflow:auto (the wrapper sized to its
	// intrinsic content, leaving empty whitespace below).
	wrapperMiniModal: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-end',
		minHeight: '100%',
		width: '100%',
	},
	wrapperQuickActionsTab: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'flex-start',
		height: '100%',
		width: '100%',
	},
	fsWrapper: {
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		// Breathing room so the greeting icon and entrance motion are not clipped by the parent scrollport.
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
	},
	insightsBannerWrapperMiniModal: {
		paddingTop: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		width: '100%',
	},
	insightsBannerWrapperMiniModalQuickActions: {
		paddingTop: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		width: '100%',
	},
	insightsBannerWrapperSidebar: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		width: '100%',
	},
});

interface RovoEmptyStateProps {
	mode: ConversationAssistantUIModes;
	/** Conversation id of the active (empty) chat, whose reasoning mode drives the greeting bucket. */
	conversationId?: string;
	handleClickDynamicPrompt?: (starter: ConversationAction) => void;
	sendMessage: (prompt: string, handleType: HandleActionType, agent?: Agent) => void;
	isResizeEnabled?: boolean;
	isResizeHeaderThresholdReached?: boolean | null;
	onDiscoverPromptLibraryClick?: () => void;
	layout?: 'default' | 'quickActionsTab';
	/** Fired when the user enters or exits a nested action menu. */
	onNestedViewChange?: (isNested: boolean) => void;
}

type RovoEmptyStateInnerProps = RovoEmptyStateProps & {
	/** `selectedSpace` read from the ui-store, threaded in by the gate wrappers below. */
	selectedSpace: ReturnType<typeof useSelectedSpaceSelector>[0]['selectedSpace'];
};

const RovoEmptyStateComponent = ({
	mode,
	conversationId,
	handleClickDynamicPrompt,
	sendMessage,
	isResizeEnabled = false,
	isResizeHeaderThresholdReached = null,
	onDiscoverPromptLibraryClick,
	layout = 'default',
	selectedSpace,
	onNestedViewChange,
}: RovoEmptyStateInnerProps): JSX.Element => {
	di(useInsightsStatus);
	const setChatInputPlaceholderOverride = usePlaceholderOverrideDispatch();

	const { closeChat } = useGlobalConfig();
	const { deepResearchEnabled, taskModeEnabled } = useChatModeState(EMPTY_CONVERSATION_ID);
	const { name } = useProfileController();
	const [{ customGreeting: runtimeCustomGreeting, skillRegistryTags, projectContext }] =
		useEmptyChatLaunchSelector();

	const hasRuntimeCustomGreeting = Boolean(runtimeCustomGreeting);
	const { fullScreen, elements, customGreeting: uiConfigCustomGreeting } = useUIConfig();
	const customGreeting = runtimeCustomGreeting ?? uiConfigCustomGreeting;
	const { setActiveAction } = useActiveAction();
	const [isInNestedActionView, setIsInNestedActionView] = useState(false);
	const { setActiveMenu } = useUIStoreActions();
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-empty-state',
	});
	const isRenderStabilizationEnabled =
		FeatureGates.getExperimentValue<boolean>(
			'rovo_ttai_segment_render_stabilization',
			'isEnabled',
			false,
		) === true;

	const personalityGreetingsEnabled =
		FeatureGates.getExperimentValue<boolean>('rovo_chat_personality_presets', 'isEnabled', false) &&
		FeatureGates.getExperimentValue<boolean>('rovo_chat_personality_greeting', 'isEnabled', false);

	const isPulseExperimentEnabled =
		!fullScreen && FeatureGates.getExperimentValue('rovo_growth_chat_pulse', 'isEnabled', false);

	const scheduleOk = isPulseExperimentEnabled
		? FeatureGates.checkGate('rovo_growth_insights_banner_ignore_schedule') ||
			isInsightsScheduledDay()
		: false;

	const { isInsightsAvailable, generatedAt, isFresh, statusFetchState } = useInsightsStatus({
		enabled: isPulseExperimentEnabled && scheduleOk,
		assistanceServiceParams,
	});

	// Report how the insights entry point resolved (shown, or why not) for per-user diagnosis.
	useReportInsightsBannerResolved({
		surface: 'quickActions',
		pulseEnabled: isPulseExperimentEnabled,
		requiresProduct: false,
		hasProduct: !!assistanceServiceParams.product,
		scheduleOk,
		statusFetchState,
		isInsightsAvailable,
		requiresFresh: layout === 'quickActionsTab',
		isFresh,
		hasSelectedSpace: !!selectedSpace,
		generatedAt,
	});

	const handleInsightsBannerClick = useCallback(() => {
		setActiveMenu('pulse');
	}, [setActiveMenu]);

	// Chain the local nested-view setter (used by the greeting-hide guard below)
	// with the parent-lifted callback (used by useEmptyStateRender to hide the
	// RovoTabs tab strip while in a nested action).
	const handleNestedViewChange = useCallback(
		(isNested: boolean) => {
			setIsInNestedActionView(isNested);
			onNestedViewChange?.(isNested);
		},
		[onNestedViewChange],
	);

	const spaceAnalyticsAttributes = useMemo(
		() =>
			projectContext?.spaceId
				? { spaceId: projectContext.spaceId, spaceName: projectContext.projectName }
				: undefined,
		[projectContext?.spaceId, projectContext?.projectName],
	);

	const firstName = name.split(' ')[0];

	const isMiniModal = mode === ConversationAssistantUIModes.MINI_MODAL;
	const isQuickActionsTab = layout === 'quickActionsTab';
	const isQuickActionsTabMiniModal = isQuickActionsTab && isMiniModal;
	const greetingHeadingSize = customGreeting?.titleSize ?? (isMiniModal ? 'small' : 'xlarge');

	const renderGreeting = (isRevealedByResize?: boolean | null, isRevealedByDefault?: boolean) => {
		if (customGreeting && !deepResearchEnabled && !taskModeEnabled) {
			return (
				<CustomGreetingCard
					src={customGreeting.iconSrc}
					srcDark={customGreeting.iconSrcDark}
					title={customGreeting.title}
					subtitle={customGreeting.subtitle}
					testId={customGreeting.testId}
					animationDuration={customGreeting.animationDuration}
					entranceDirection={customGreeting.entranceDirection}
					exitDirection={customGreeting.exitDirection}
					animationKey={customGreeting.animationKey}
					headingSize={greetingHeadingSize}
					isRevealedByResize={isRevealedByResize}
					isRevealedByDefault={isRevealedByDefault}
					firstName={firstName}
					truncateGreeting={customGreeting.truncateGreeting}
					imageHeight={customGreeting.imageHeight}
				/>
			);
		}
		if (personalityGreetingsEnabled) {
			return (
				<PersonalityRovoGreeting
					firstName={firstName}
					conversationId={conversationId ?? EMPTY_CONVERSATION_ID}
					headingSize={greetingHeadingSize}
					isRevealedByResize={isRevealedByResize}
					isRevealedByDefault={isRevealedByDefault}
					assistanceServiceParams={assistanceServiceParams}
				/>
			);
		}
		return (
			<RovoGreeting
				firstName={firstName}
				isDeepResearchEnabled={deepResearchEnabled}
				isTaskModeEnabled={taskModeEnabled}
				headingSize={greetingHeadingSize}
				isRevealedByResize={isRevealedByResize}
				isRevealedByDefault={isRevealedByDefault}
			/>
		);
	};

	if (fullScreen) {
		const shouldShowFullScreenGreeting = elements?.shouldShowGreeting !== false;

		return (
			<div css={[styles.fsWrapper]} data-testid="rovo-empty-state-fullscreen">
				{shouldShowFullScreenGreeting && renderGreeting()}
			</div>
		);
	}

	const shouldShowGreeting =
		!(isMiniModal && deepResearchEnabled) && // Don't show greeting in mini modal if deep research is enabled
		(hasRuntimeCustomGreeting ||
			(elements?.shouldShowGreeting !== false &&
				(elements?.shouldShowGreeting === true || !isMiniModal)));

	const isMiniModalResizeEnabled = isMiniModal && isResizeEnabled;

	const isGreetingControlledByResize = isMiniModalResizeEnabled && !shouldShowGreeting;

	// There are two modes:
	// 1. "Visible by default" (FF on): Greeting shows initially, user can hide by resizing down
	// 2. "Controlled by resize" (FF off): Greeting hidden initially, user can reveal by resizing up

	const shouldRenderGreeting = shouldShowGreeting || isGreetingControlledByResize;
	const visibleByDefault = shouldShowGreeting;
	const shouldShowConversationActions = elements?.shouldShowConversationActions !== false;

	// In the tabbed Quick Actions layout, only show fresh insights. The legacy no-tabs empty
	// state keeps showing the banner whenever insights are available.
	const showInsightsBanner =
		isPulseExperimentEnabled &&
		isInsightsAvailable &&
		!selectedSpace &&
		(!isQuickActionsTab || isFresh);
	const resizeVisibility = isMiniModalResizeEnabled ? isResizeHeaderThresholdReached : undefined;
	const useQuickActionsTabMiniModalLayout = isQuickActionsTabMiniModal && showInsightsBanner;
	const insightsBanner = showInsightsBanner ? (
		<UFOSegment name={'insights-banner'}>
			<Box
				xcss={
					isQuickActionsTabMiniModal
						? styles.insightsBannerWrapperMiniModalQuickActions
						: isMiniModal
							? styles.insightsBannerWrapperMiniModal
							: styles.insightsBannerWrapperSidebar
				}
			>
				<InsightsBanner
					onClick={handleInsightsBannerClick}
					appearance="quickActions"
					generatedAt={generatedAt}
				/>
			</Box>
		</UFOSegment>
	) : null;

	const conversationActions = (
		<RovoEmptyStateConversationActions
			closeChat={closeChat}
			sendMessage={sendMessage}
			setChatInputPlaceholderOverride={setChatInputPlaceholderOverride}
			handleClickDynamicPrompt={handleClickDynamicPrompt}
			mode={mode}
			assistanceServiceParams={assistanceServiceParams}
			setActiveAction={setActiveAction}
			onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
			analyticsAttributes={spaceAnalyticsAttributes}
			onNestedViewChange={handleNestedViewChange}
			skillRegistryTags={skillRegistryTags}
			isRenderStabilizationEnabled={isRenderStabilizationEnabled}
		/>
	);

	return (
		<div
			css={[
				useQuickActionsTabMiniModalLayout
					? styles.wrapperQuickActionsTab
					: isMiniModalResizeEnabled
						? styles.wrapperMiniModal
						: styles.wrapper,
			]}
			data-testid="rovo-empty-state"
		>
			{shouldRenderGreeting &&
				!isInNestedActionView &&
				renderGreeting(resizeVisibility, visibleByDefault)}
			{insightsBanner}

			<UFOSegment name="rovo-conversation-actions">
				{shouldShowConversationActions && conversationActions}
			</UFOSegment>
		</div>
	);
};

// rovo_ttai_segment_render_stabilization: absorb parent render waves when props are stable.
const MemoizedRovoEmptyState = memo(RovoEmptyStateComponent, shallowEqualObjects);

// rovo_ttai_segment_render_stabilization ON: subscribe to `selectedSpace` via the
// narrow selector so the component only re-renders when `selectedSpace` changes,
// rather than on every UI-store state change.
const RovoEmptyStateWithSelector = (props: RovoEmptyStateProps): JSX.Element => {
	di(useSelectedSpaceSelector);
	const [{ selectedSpace }] = useSelectedSpaceSelector();
	return <MemoizedRovoEmptyState {...props} selectedSpace={selectedSpace} />;
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the component re-rendered on any
// UI-store state change.
const RovoEmptyStateWithBroadStore = (props: RovoEmptyStateProps): JSX.Element => {
	di(useUIStore);
	const { selectedSpace } = useUIStore();
	return <RovoEmptyStateComponent {...props} selectedSpace={selectedSpace} />;
};

export const RovoEmptyState = (props: RovoEmptyStateProps): JSX.Element => {
	return FeatureGates.getExperimentValue<boolean>(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) === true ? (
		<RovoEmptyStateWithSelector {...props} />
	) : (
		<RovoEmptyStateWithBroadStore {...props} />
	);
};
